/**
 * <ActivitiesSection> — le bloc des activités : le titre, « Faites », « À faire » (template).
 *
 * C'est le corps de `TodoListScreen.js`, celui de l'onglet « Activités » (`<ActivitiesPage>`),
 * du parent comme de l'élève. L'ordre du legacy, à l'identique : le titre et ses gestes, puis « Faites »
 * (repliable, cinq lignes, « Voir plus »), puis « À faire » et sa carte-liste, que ferme
 * « Générer une activité ».
 *
 * Ce que chaque espace peut y faire se dit par les callbacks qu'il passe, jamais par un rôle :
 *  - le parent sélectionne et archive (`onStartSelection`…), ouvre les archives (`onOpenArchived`) ;
 *  - l'élève n'a ni l'un ni l'autre : il lance l'activité qu'il touche (`onOpenActivity`), rouvre
 *    celle qu'il a finie (`onOpenDoneActivity`), et range sa liste en la glissant (`onReorder`).
 *
 * Contrôlé : la sélection arrive en props, l'ordre final remonte par `onReorder`. Le pli du bloc
 * « Faites » et le glisser en cours sont de l'état d'interface, donc ils vivent ici
 * (`useTodoReorderGesture`) ; `reorder` montre le glisser figé, pour une story. La barre
 * « Archiver (N) » est le pied de la PAGE : ce bloc ne la monte pas.
 */
import React, { useMemo } from "react"
import { Platform, View } from "react-native"
import { GestureDetector } from "react-native-gesture-handler"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Title } from "../../atoms/typography/Title"
import { ActivityRow } from "../../molecules/ActivityRow"
import { CardList, CardListDropSlot, CardListLinkRow } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { RefreshButton } from "../../molecules/RefreshButton"
import { SelectionHeaderRow } from "../../molecules/SelectionHeaderRow"
import { ThreeDotMenu } from "../../molecules/ThreeDotMenu"
import { type Voice } from "../../utils/voice"
import { type UIActivity } from "../../../ports/primary/ui/types"
import { DoneActivitiesAccordion, type UIDoneActivitiesHistory } from "./DoneActivitiesAccordion"
import { REORDER_BY_HANDLE, REORDER_HANDLE_WIDTH, useTodoReorderGesture } from "./useTodoReorderGesture"

/** Les mots de la liste vide, par voix — ceux de `TodoListScreen.js` (`TEXTS`), au mot près. */
const TEXTS: Record<Voice, { emptyTitle: string; emptyMessage: string }> = {
    parent: { emptyTitle: "Lancez une activité", emptyMessage: "La liste est vide pour l'instant..." },
    student: { emptyTitle: "Lance une activité !", emptyMessage: "Ta liste est vide pour l'instant..." },
}

/**
 * L'identifiant d'une activité, nommé : un type indexé dans les props d'une story fait tomber l'outil
 * de documentation de Storybook.
 */
export type ActivityId = UIActivity["id"]

/**
 * Une activité en cours de déplacement dans « À faire » : laquelle, et la place où elle tombera
 * (l'index dans la liste une fois la ligne retirée de sa place d'origine).
 */
export type ActivitiesReorder = {
    activityId: ActivityId
    toIndex: number
}

export type ActivitiesSectionProps = {
    /** Les activités à faire, dans l'ordre de la liste. */
    activities: UIActivity[]
    /**
     * Les activités récemment faites — le legacy en montre cinq au plus (celles du jour, puis les
     * deux dernières d'avant).
     */
    doneActivities?: UIActivity[]
    /**
     * Le bloc « Faites » est affiché. Défaut : seulement quand `doneActivities` n'est pas vide.
     * L'élève le garde dès qu'il a déjà travaillé (son XP, dans le legacy), même sans
     * activité récente : c'est son chemin vers ses réalisations.
     */
    showDone?: boolean
    /** Le bloc « Faites » s'ouvre plié, comme le legacy ; une story peut l'ouvrir d'emblée. */
    defaultDoneExpanded?: boolean

    /** Toucher une activité (hors sélection multiple) : sa fiche pour le parent, son lancement pour l'élève. */
    onOpenActivity?: (id: ActivityId) => void
    /** Toucher une activité FAITE. Absent, c'est `onOpenActivity` qui la reçoit. */
    onOpenDoneActivity?: (id: ActivityId) => void
    /** « Voir plus », sous les activités faites — ouvre les réalisations. */
    onSeeAllDone?: () => void
    /** Le « + » du legacy — ouvre « Générer une activité ». */
    onGenerate?: () => void
    /** Le menu de la page — « Activités archivées ». */
    onOpenArchived?: () => void

    // ── Sélection multiple (contrôlée) ──
    selectionMode?: boolean
    selectedIds?: ActivityId[]
    /** Appui long sur une ligne, ou « Sélectionner » : la sélection s'ouvre sur cette activité. */
    onStartSelection?: (id?: ActivityId) => void
    onToggleSelect?: (id: ActivityId) => void
    onToggleSelectAll?: () => void
    onCancelSelection?: () => void

    /**
     * Une ligne de « À faire » est en cours de déplacement : elle se soulève au-dessus de sa place
     * d'arrivée. Un état d'affichage figé, pour une story : le glisser réel tient le sien. Absent :
     * la liste au repos.
     */
    reorder?: ActivitiesReorder | null
    /**
     * « À faire » se range au doigt : un appui long sur la ligne au téléphone, sa poignée sur le web.
     * Reçoit tous les identifiants, dans l'ordre que l'élève vient de poser — une fois, au lâcher, et
     * seulement si la ligne a changé de place. Absent (défaut), la liste ne se glisse pas et ne
     * montre pas de poignée ; elle ne se glisse pas non plus pendant une sélection.
     */
    onReorder?: (ids: ActivityId[]) => void

    /** Un rafraîchissement est en cours : le bouton de la barre de titre tourne. */
    refreshing?: boolean
    onRefresh?: () => void
    /** À qui le bloc parle — `"parent"` par défaut. */
    voice?: Voice
    /** L'intitulé du bloc des faites. Défaut « Faites ». */
    doneTitle?: string
    /** Les activités terminées, à déplier dans le bloc des faites, au lieu de `onSeeAllDone`. */
    doneHistory?: UIDoneActivitiesHistory
    /** Pas de titre « Activités » : un onglet le dit déjà. Les gestes du titre restent. Défaut `false`. */
    hideTitle?: boolean
    testID?: string
}

export function ActivitiesSection(props: ActivitiesSectionProps) {
    const {
        activities,
        doneActivities = [],
        showDone = doneActivities.length > 0,
        defaultDoneExpanded = false,
        onOpenActivity,
        onOpenDoneActivity = onOpenActivity,
        onSeeAllDone,
        onGenerate,
        onOpenArchived,
        selectionMode = false,
        selectedIds = [],
        onStartSelection,
        onToggleSelect,
        onToggleSelectAll,
        onCancelSelection,
        reorder,
        onReorder,
        refreshing = false,
        onRefresh,
        voice = "parent",
        hideTitle = false,
        doneTitle,
        doneHistory,
        testID,
    } = props

    const texts = TEXTS[voice]
    const isEmpty = activities.length === 0
    const selectedCount = selectedIds.length
    const allSelected = !isEmpty && selectedCount === activities.length
    const showGenerate = !selectionMode && onGenerate != null

    const pressRow = (activity: UIActivity) => {
        if (selectionMode) onToggleSelect?.(activity.id)
        else onOpenActivity?.(activity.id)
    }

    const ids = useMemo(() => activities.map((activity) => activity.id), [activities])
    const drag = useTodoReorderGesture(ids, selectionMode ? undefined : onReorder)
    const shownReorder = drag.drag ?? reorder

    // La ligne qu'on déplace quitte sa place et se pose, soulevée, sur celle où elle tombera.
    const moved =
        shownReorder != null ? activities.find((activity) => activity.id === shownReorder.activityId) : undefined
    const resting = moved != null ? activities.filter((activity) => activity.id !== moved.id) : activities
    const dropIndex =
        moved != null && shownReorder != null ? Math.min(Math.max(shownReorder.toIndex, 0), resting.length) : -1
    const dropSlot =
        moved != null ? (
            <CardListDropSlot key="drop-slot">
                <ActivityRow activity={moved} lifted />
            </CardListDropSlot>
        ) : null

    const renderRow = (activity: UIActivity) => (
        <ActivityRow
            key={activity.id}
            activity={activity}
            selectable={selectionMode}
            selected={selectedIds.includes(activity.id)}
            onPress={pressRow}
            onLongPress={selectionMode || onStartSelection == null ? undefined : (item) => onStartSelection(item.id)}
        />
    )
    // Glissante, la ligne se mesure (le doigt se lit sur les hauteurs) et, sur le web, porte sa
    // poignée à gauche. Sinon, la ligne nue, à l'identique.
    const renderTodoRow = (activity: UIActivity) =>
        drag.enabled ? (
            <View
                key={activity.id}
                onLayout={(event) => drag.measure(activity.id, event)}
                style={REORDER_BY_HANDLE ? styles.handleRow : undefined}
            >
                {REORDER_BY_HANDLE && (
                    <View
                        style={styles.handle}
                        accessibilityElementsHidden
                        importantForAccessibility="no-hide-descendants"
                    >
                        <Icon name="grip-vertical" size={20} color="text-muted" />
                    </View>
                )}
                <View style={styles.rowBody}>{renderRow(activity)}</View>
            </View>
        ) : (
            renderRow(activity)
        )

    const todoRows = resting.flatMap((activity, index) => [
        index === dropIndex ? dropSlot : null,
        renderTodoRow(activity),
    ])
    if (dropIndex === resting.length) todoRows.push(dropSlot)

    const todoCard = (
        <CardList>
            {todoRows}
            {showGenerate && <CardListLinkRow iconName="plus" label="Générer une activité" onPress={onGenerate} />}
        </CardList>
    )

    return (
        <Stack gap="6" testID={testID}>
            <Row justify={hideTitle ? "end" : "between"} align="center">
                {!hideTitle && <Title size="text-2xl">Activités</Title>}
                <Row gap="1" align="center">
                    {onRefresh != null && <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />}
                    {onOpenArchived != null && (
                        <ThreeDotMenu
                            items={[{ icon: "archive", text: "Activités archivées", onPress: onOpenArchived }]}
                        />
                    )}
                </Row>
            </Row>

            {showDone && (
                <DoneActivitiesAccordion
                    activities={doneActivities}
                    defaultExpanded={defaultDoneExpanded}
                    onPress={onOpenDoneActivity != null ? (activity) => onOpenDoneActivity(activity.id) : undefined}
                    onSeeAll={onSeeAllDone}
                    title={doneTitle}
                    history={doneHistory}
                />
            )}

            <Stack gap="3">
                <SelectionHeaderRow
                    selectionMode={selectionMode}
                    selectedCount={selectedCount}
                    allSelected={allSelected}
                    onCancelSelection={onCancelSelection}
                    onToggleSelectAll={isEmpty ? undefined : onToggleSelectAll}
                    onStartSelection={isEmpty || onStartSelection == null ? undefined : () => onStartSelection()}
                >
                    <Row gap="2" align="center">
                        <SectionLabel>À faire</SectionLabel>
                        <Badge text={String(activities.length)} variant="primary" tinted />
                    </Row>
                </SelectionHeaderRow>

                {isEmpty && <EmptyState title={texts.emptyTitle} message={texts.emptyMessage} />}

                {/* La liste vide garde son « + » : c'est le seul geste de l'écran. */}
                {(!isEmpty || showGenerate) &&
                    (drag.enabled ? (
                        // Le geste tient sur la carte entière, pas sur une ligne : celle qu'on déplace
                        // quitte sa place, et un geste posé sur elle mourrait avec. Sur le web, la
                        // carte laisse défiler la page au doigt ; seule la poignée le garde.
                        <GestureDetector gesture={drag.gesture} touchAction="pan-y">
                            <View collapsable={false}>{todoCard}</View>
                        </GestureDetector>
                    ) : (
                        todoCard
                    ))}
            </Stack>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Une ligne glissante sur le web : la poignée, puis la ligne. */
    handleRow: {
        flexDirection: "row",
    },
    /** La poignée, dans la bande où le geste démarre (`REORDER_HANDLE_WIDTH`). */
    handle: {
        width: REORDER_HANDLE_WIDTH,
        paddingLeft: theme.spacing["3"],
        justifyContent: "center",
        ...(Platform.OS === "web" ? ({ cursor: "grab", touchAction: "none" } as object) : null),
    },
    rowBody: {
        flex: 1,
    },
}))
